import { Service, inject, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { ApiService } from '@app/services/api.service';
import { environment } from '@src/environments/environment';
import { DrupalService } from '@app/services/drupal.service';
import { AuthService } from '@app/services/auth.service';

@Service()
export class GaService {
  currentGa = signal<any>(null);
  autocompleteList = signal<any[]>([]);

  #http = inject(HttpClient);
  #doc = inject(DOCUMENT);
  #api = inject(ApiService);
  #drupal = inject(DrupalService);
  #auth = inject(AuthService);

  // Result per email, so the lookup runs once per session. A null value means
  // the Marktbearbeiter has no published person page.
  #marktbearbeiterByEmail = new Map<string, any>();
  #lastZip = '';

  // returns the mobiliar domain in any case
  get #apiUrl() {
    const urls: any = environment.domains.mobiliar || [];
    return urls[this.#drupal.language()] || urls['de'];
  }

  async updateAutocomplete(query: string): Promise<void> {
    try {
      const autocompleteUrl = `${this.#apiUrl}/api/ziploc/plzOrtTagsLimit/`;
      const { param } = await lastValueFrom(this.#http.get<any>(autocompleteUrl + query));
      this.autocompleteList.set(param);
    } catch (e) {
      this.#handleError(e);
    }
  }

  async selectGa(zip: string, useMarktbearbeiter = false): Promise<void> {
    this.#lastZip = zip;
    const marktbearbeiter = useMarktbearbeiter
      ? await this.#selectMarktbearbeiter()
      : null;
    if (marktbearbeiter) {
      this.currentGa.set(marktbearbeiter);
      return;
    }

    try {
      const detailsUrl = `${this.#apiUrl}/api/ziploc/ga/`;
      const details: any = await lastValueFrom(
        this.#http.get<any>(detailsUrl + zip, {
          withCredentials: this.#api.loggedIn(),
        })
      );
      if (details?.param) this.currentGa.set(details.param[0] || null);
    } catch (e) {
      this.#handleError(e);
    }
  }

  async selectMarktbearbeiterForCurrentZip(): Promise<void> {
    if (!this.#lastZip) return;
    await this.selectGa(this.#lastZip, true);
  }

  /**
   * Contact details of the Marktbearbeiter of the logged in customer.
   *
   * The Marktbearbeiter is the employee who owns the customer relationship. If
   * that person has a published person page, the GA Box shows them instead of
   * the general agency responsible for the zip code.
   *
   * @returns the contact details, or null when there is nobody to show.
   */
  async #selectMarktbearbeiter(): Promise<any> {
    const email = this.#auth.userInfo()?.mbaEmail;
    if (!email) return null;
    if (this.#marktbearbeiterByEmail.has(email)) {
      return this.#marktbearbeiterByEmail.get(email);
    }

    let person = null;
    try {
      const personUrl = `${this.#apiUrl}/api/ziploc/person/`;
      const details: any = await lastValueFrom(
        this.#http.get<any>(personUrl + encodeURIComponent(email), {
          withCredentials: this.#api.loggedIn(),
        })
      );
      person = details?.param?.[0] || null;
    } catch (e) {
      // Falling back to the zip code lookup is enough, the box stays filled.
      this.#handleError(e);
    }
    this.#marktbearbeiterByEmail.set(email, person);

    return person;
  }

  #handleError(e: any) {
    console.error(e);
    this.#doc?.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }
}
